import type { AiActionNote, AiActionStatus, AiActionTarget, AiChange, AiPlanStatus, AiPlanStepWire, AiUntrackedWrite } from '@roll1/shared';
import type { AiChatMessage } from './useAiChat';

/**
 * What the frames of an answer do to the list of a chat's items (group E): the change card, the plan card with
 * «Do it», the button outward. Pure functions of the list, shared by the chat hook and the @Tia cards under a reply.
 */

/** untracked: what the answer may have written that its list does not hold (no undo for it). */
export interface TurnFrame { turnId: string; planId?: string | undefined; changes: AiChange[]; untracked?: AiUntrackedWrite[] | undefined; timestamp?: string | undefined }

/** The card of an answer: new, or replaced (after an undo). A card of a plan applied by «Do it» closes that plan's card. */
export function itemsWithTurn(prev: AiChatMessage[], turn: TurnFrame): AiChatMessage[] {
  let next = prev;
  if (turn.planId !== undefined) {
    next = next.map((m) => {
      const plan = m.planData;
      return m.role === 'plan' && plan !== undefined && plan.planId === turn.planId && plan.state !== 'applied' ? { ...m, planData: { ...plan, state: 'applied' as const } } : m;
    });
  }
  const idx = next.findIndex((m) => m.role === 'changes' && m.turnData?.turnId === turn.turnId);
  const turnData = { turnId: turn.turnId, changes: turn.changes, ...(turn.untracked !== undefined && turn.untracked.length > 0 ? { untracked: turn.untracked } : {}) };
  if (idx >= 0) {
    const copy = [...next];
    copy[idx] = { ...copy[idx]!, turnData };
    return copy;
  }
  return [...next, { id: crypto.randomUUID(), role: 'changes', content: '', turnData, timestamp: turn.timestamp ?? new Date().toISOString() }];
}

export function itemsWithPlan(prev: AiChatMessage[], frame: { planId: string; steps: AiPlanStepWire[]; timestamp?: string | undefined }): AiChatMessage[] {
  const idx = prev.findIndex((m) => m.role === 'plan' && m.planData?.planId === frame.planId);
  const planData = { planId: frame.planId, steps: frame.steps, state: 'open' as const };
  if (idx >= 0) {
    const copy = [...prev];
    copy[idx] = { ...copy[idx]!, planData };
    return copy;
  }
  return [...prev, { id: crypto.randomUUID(), role: 'plan', content: '', planData, timestamp: frame.timestamp ?? new Date().toISOString() }];
}

export function itemsWithPlanStale(prev: AiChatMessage[], frame: { planId: string; reason: 'beat_changed' | 'objective_changed' | 'missing' }): AiChatMessage[] {
  return prev.map((m) => (m.role === 'plan' && m.planData?.planId === frame.planId && m.planData.state !== 'applied'
    ? { ...m, planData: { ...m.planData, state: 'stale' as const, staleReason: frame.reason } }
    : m));
}

export function itemsWithPlanDismissed(prev: AiChatMessage[], planId: string): AiChatMessage[] {
  return prev.map(m => m.planData?.planId === planId ? { ...m, planData: { ...m.planData, state: 'dismissed' as const } } : m);
}

export function aiResultKey(message: AiChatMessage): string | undefined {
  return message.turnData ? `turn:${message.turnData.turnId}` : message.planData ? `plan:${message.planData.planId}` : undefined;
}

export function visibleAiMessages(messages: AiChatMessage[], hidden: readonly string[] = []): AiChatMessage[] {
  return messages.filter(m => m.planData?.state !== 'applied' && !hidden.includes(aiResultKey(m) ?? ''));
}

export function itemsWithOutward(prev: AiChatMessage[], frame: { actionId: string; kind: 'show' | 'delete'; tool: string; target: { kind: 'journal' | 'page' | 'location'; id: string; name: string }; timestamp?: string | undefined; fromUntrustedSource?: true | 'unknown' | undefined }): AiChatMessage[] {
  if (prev.some((m) => m.role === 'outward' && m.outwardData?.actionId === frame.actionId)) return prev;
  return [...prev, {
    id: crypto.randomUUID(), role: 'outward', content: '',
    outwardData: { actionId: frame.actionId, kind: frame.kind, tool: frame.tool, target: frame.target, done: false, ...(frame.fromUntrustedSource !== undefined ? { fromUntrustedSource: frame.fromUntrustedSource } : {}) },
    timestamp: frame.timestamp ?? new Date().toISOString(),
  }];
}

export function itemsWithConfirmed(prev: AiChatMessage[], frame: { actionId: string }): AiChatMessage[] {
  return prev.map((m) => (m.role === 'outward' && m.outwardData?.actionId === frame.actionId
    ? { ...m, outwardData: { ...m.outwardData, done: true } }
    : m));
}

export interface ActionStatusFrame {
  actionId: string; status: AiActionStatus; kind: 'show' | 'delete'; tool: string; target: AiActionTarget;
  note?: AiActionNote | undefined; replacedBy?: string | undefined; timestamp?: string | undefined;
}

/**
 * P5: what the server's record says became of a button outward. The card is drawn from the frame when it is not there
 * yet (after a reload). Only `pending` leaves a pressable button; `done` is the old «done».
 */
export function itemsWithActionStatus(prev: AiChatMessage[], frame: ActionStatusFrame): AiChatMessage[] {
  const status = { status: frame.status, done: frame.status === 'done', stale: false, ...(frame.note !== undefined ? { note: frame.note } : {}), ...(frame.replacedBy !== undefined ? { replacedBy: frame.replacedBy } : {}) };
  if (prev.some((m) => m.role === 'outward' && m.outwardData?.actionId === frame.actionId)) {
    return prev.map((m) => (m.role === 'outward' && m.outwardData?.actionId === frame.actionId
      ? { ...m, outwardData: { ...m.outwardData, ...status } }
      : m));
  }
  return [...prev, {
    id: crypto.randomUUID(), role: 'outward', content: '',
    outwardData: { actionId: frame.actionId, kind: frame.kind, tool: frame.tool, target: frame.target, ...status },
    timestamp: frame.timestamp ?? new Date().toISOString(),
  }];
}

/** P5: what became of a plan applied with «Do it» (open again, being applied, applied, or unknown with how much went back). */
export function itemsWithPlanStatus(prev: AiChatMessage[], frame: { planId: string; status: AiPlanStatus; rolledBack?: 'all' | 'partial' | 'none' | undefined }): AiChatMessage[] {
  return prev.map((m) => {
    const plan = m.planData;
    if (m.role !== 'plan' || plan === undefined || plan.planId !== frame.planId) return m;
    if (frame.status === 'unknown') return { ...m, planData: { ...plan, state: 'unknown' as const, ...(frame.rolledBack !== undefined ? { rolledBack: frame.rolledBack } : {}) } };
    return { ...m, planData: { ...plan, state: frame.status } };
  });
}
